import React, { useRef, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useDebounce } from 'use-debounce';
import PropTypes from 'prop-types';
import moment from 'moment';
import { get, size, isEmpty, isUndefined } from 'lodash/fp';
import BarcodeReader from 'react-barcode-reader';
import {
  FlexboxGrid,
  SelectPicker,
  Input,
  Col,
  InputGroup,
  Icon
} from 'rsuite';
import { fetchPosLocationOptions } from '../../../../redux/sagas/routines';
import { getOrderForVisitTrackId } from '../../../../redux/POS/selectors/order';
import {
  getSortedPosLocationOptions,
  getIsFetchedPosLocationOptions
} from '../../../../redux/POS/selectors/options';
import { parseInt } from '../../../../helpers/utils';
import { clearPosSearchItems } from '../../../../redux/POS/actions';

const ScanChangeInterval = 10;

function SearchForm({ onSearch, customLocationOptions, startingLocation }) {
  const isScanSearch = useRef(false);
  const inputChangeInterval = useRef(0);
  const [searchInput, setSearchInput] = useState('');
  const [inventoryLocationId, setInventoryLocationId] = useState(null);
  const [q] = useDebounce(searchInput, 800);
  const visitTrackId = parseInt(get('visitTrackId', useParams()));
  const order = useSelector(state =>
    getOrderForVisitTrackId(state, visitTrackId)
  );
  const trackingMethodId = get('trackingMethodId', order);
  const isFetchedLocationOptions = useSelector(getIsFetchedPosLocationOptions);
  const defaultLocationOptions = useSelector(getSortedPosLocationOptions);
  const locationOptions = useMemo(
    () =>
      !isEmpty(customLocationOptions)
        ? customLocationOptions
        : defaultLocationOptions,
    [defaultLocationOptions, customLocationOptions]
  );

  const localStorageLocationKey = `location_${trackingMethodId}`;
  const setLocalStorageLocation = locationId => {
    localStorage.setItem(localStorageLocationKey, locationId || '');
  };
  const getLocalStorageLocation = () => {
    const locationId = localStorage.getItem(localStorageLocationKey);
    return locationId ? parseInt(locationId) : null;
  };

  const dispatch = useDispatch();
  useEffect(
    () => {
      if (!isFetchedLocationOptions) {
        dispatch(fetchPosLocationOptions());
      }
    },
    [isFetchedLocationOptions]
  );

  useEffect(
    () => {
      if (isUndefined(startingLocation)) return;
      setInventoryLocationId(startingLocation || getLocalStorageLocation());
    },
    [startingLocation]
  );

  // Execute search
  useEffect(
    () => {
      if (!isScanSearch.current && q) {
        onSearch({ inventoryLocationId, q }, isScanSearch.current);
      }
    },
    [q, inventoryLocationId]
  );

  // Clear search result
  useEffect(
    () => {
      if (!searchInput) onSearch({ inventoryLocationId, q: '' });
    },
    [searchInput]
  );

  const isScanChangeInterval = () => {
    const currTime = parseInt(moment().format('x'));
    const prevTime = inputChangeInterval.current;
    const changeInterval = currTime - prevTime;
    inputChangeInterval.current = currTime;
    return changeInterval <= ScanChangeInterval;
  };

  const handleScan = value => {
    setSearchInput(value);
    isScanSearch.current = true;
    onSearch({ inventoryLocationId, q: value }, isScanSearch.current);
  };

  const handleInputChange = value => {
    setSearchInput(value);
  };

  const handleInputKeyUp = ({ keyCode, target }) => {
    const { value } = target;
    isScanSearch.current = false;
    const isEnterKey = keyCode === 13;
    const validBarcodeLength = size(value) >= 6;
    if (isScanChangeInterval() && isEnterKey && validBarcodeLength) {
      handleScan(value.replace(q, ''));
    }
  };

  const handleLocationChange = value => {
    setInventoryLocationId(value);
    setLocalStorageLocation(value);
  };

  const handleClearSearchInput = () => {
    setSearchInput('');
    document.querySelector('#searchInput').focus();
    dispatch(clearPosSearchItems());
  };

  return (
    <FlexboxGrid>
      <FlexboxGrid.Item
        componentClass={Col}
        xs={24}
        sm={12}
        style={{ marginBottom: 10 }}
      >
        <InputGroup inside>
          <Input
            id="searchInput"
            onKeyUp={handleInputKeyUp}
            onChange={handleInputChange}
            value={searchInput}
            placeholder="Scan or type the item name or description"
          />
          {searchInput && (
            <InputGroup.Button disabled={!q} onClick={handleClearSearchInput}>
              <Icon icon="close" />
            </InputGroup.Button>
          )}
        </InputGroup>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        componentClass={Col}
        xs={24}
        sm={12}
        style={{ marginBottom: 10 }}
      >
        <SelectPicker
          data={locationOptions}
          onChange={handleLocationChange}
          value={inventoryLocationId}
          style={{ width: '100%' }}
          placeholder={
            !isFetchedLocationOptions ? 'Loading...' : 'Filter Location'
          }
        />
        <BarcodeReader onScan={handleScan} />
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
}

SearchForm.propTypes = {
  onSearch: PropTypes.func.isRequired,
  customLocationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  startingLocation: PropTypes.number
};

SearchForm.defaultProps = {
  startingLocation: undefined
};

export default SearchForm;
